import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { relTime } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/layout/PageHeader";
import { SCHED } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Copy, Plus, Trash2, MapPin, Video, User, Calendar } from "lucide-react";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export default function Scheduler() {
  const { user } = useAuth();
  const publicUrl = `${window.location.origin}/book/${user?.slug}`;
  const chatUrl = `${window.location.origin}/chat/agent/${user?.slug}`;

  return (
    <div>
      <PageHeader
        overline="Booking"
        title="Meeting Scheduler"
        subtitle="Share one link. Never double-book."
        actions={
          <Button
            variant="outline"
            className="rounded-sm"
            onClick={() => { navigator.clipboard.writeText(publicUrl).then(() => toast.success("Booking link copied")).catch(() => toast.error("Copy failed")); }}
          >
            <Copy className="h-4 w-4 mr-2" />Copy Booking Link
          </Button>
        }
      />

      <div className="px-6 lg:px-10 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="p-4 rounded-sm border border-border bg-card">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold flex items-center gap-1"><Calendar className="h-3 w-3" />Your booking link</div>
            <div className="font-mono text-sm mt-1 text-primary break-all">{publicUrl}</div>
          </div>
          <div className="p-4 rounded-sm border border-primary/40 bg-primary/5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Your direct AI chat link</div>
                <div className="font-mono text-sm mt-1 text-primary break-all">{chatUrl}</div>
                <div className="text-[10px] text-muted-foreground mt-1">Escalations auto-route to you.</div>
              </div>
              <button
                onClick={() => { navigator.clipboard.writeText(chatUrl).then(() => toast.success("Chat link copied")).catch(() => toast.error("Copy failed")); }}
                className="p-2 rounded-sm border border-border hover:bg-secondary/60"
                data-testid="copy-chat-link"
              ><Copy className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        </div>

        <Tabs defaultValue="types">
          <TabsList className="rounded-sm bg-secondary/40">
            <TabsTrigger value="types" className="rounded-sm">Meeting Types</TabsTrigger>
            <TabsTrigger value="availability" className="rounded-sm">Availability</TabsTrigger>
            <TabsTrigger value="booked" className="rounded-sm">Booked</TabsTrigger>
          </TabsList>

          <TabsContent value="types" className="mt-6"><MeetingTypesTab /></TabsContent>
          <TabsContent value="availability" className="mt-6"><AvailabilityTab /></TabsContent>
          <TabsContent value="booked" className="mt-6"><BookedTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function MeetingTypesTab() {
  const { data: types = [] } = useQuery({ queryKey: ["mtypes"], queryFn: () => api.get("/meetings/types").then((r) => r.data) });
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const del = useMutation({
    mutationFn: (id) => api.delete(`/meetings/types/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mtypes"] }); toast.success("Deleted"); },
  });

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid={SCHED.addTypeButton} className="rounded-sm bg-primary hover:bg-primary/90"><Plus className="h-4 w-4 mr-2" />Add Meeting Type</Button>
          </DialogTrigger>
          <AddMeetingTypeDialog onClose={() => setOpen(false)} />
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {types.length === 0 && <Card className="col-span-full rounded-sm bg-card border-border p-8 text-center text-sm text-muted-foreground">No meeting types yet. Create one to start receiving bookings.</Card>}
        {types.map((t) => (
          <Card key={t.id} className="rounded-sm bg-card border-border p-5">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">{t.duration_minutes} min</div>
                <div className="font-display text-lg font-extrabold">{t.name}</div>
              </div>
              <button onClick={() => del.mutate(t.id)} className="text-muted-foreground hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
            {t.description && <div className="text-xs text-muted-foreground mb-3">{t.description}</div>}
            <div className="flex flex-wrap gap-1.5">
              {(t.locations || []).map((l, i) => (
                <Badge key={i} variant="secondary" className="rounded-sm text-[10px]">
                  {l.type === "online" ? <Video className="h-2.5 w-2.5 mr-1 inline" /> : l.type === "client" ? <User className="h-2.5 w-2.5 mr-1 inline" /> : <MapPin className="h-2.5 w-2.5 mr-1 inline" />}
                  {l.label}
                </Badge>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function AddMeetingTypeDialog({ onClose }) {
  const [name, setName] = useState("Discovery Call");
  const [dur, setDur] = useState(30);
  const [desc, setDesc] = useState("");
  const [locations, setLocations] = useState([
    { type: "online", label: "Google Meet (auto-generated)" },
    { type: "office", label: "Main Office", address: "" },
    { type: "client", label: "Client-suggested location" },
  ]);
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => api.post("/meetings/types", { name, duration_minutes: Number(dur), description: desc, locations }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mtypes"] }); toast.success("Meeting type added"); onClose(); },
  });
  return (
    <DialogContent className="rounded-sm max-w-lg">
      <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold tracking-tight">Add Meeting Type</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Name</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div><Label>Duration (min)</Label><Input type="number" className="rounded-sm mt-1.5 bg-secondary/40" value={dur} onChange={(e) => setDur(e.target.value)} /></div>
        <div><Label>Description</Label><Textarea rows={2} className="rounded-sm mt-1.5 bg-secondary/40" value={desc} onChange={(e) => setDesc(e.target.value)} /></div>
        <div>
          <Label>Locations</Label>
          <div className="space-y-2 mt-1.5">
            {locations.map((l, i) => (
              <div key={i} className="flex gap-2 items-center">
                <select value={l.type} onChange={(e) => { const ll = [...locations]; ll[i].type = e.target.value; setLocations(ll); }} className="rounded-sm bg-secondary/40 border border-border px-2 py-1.5 text-sm">
                  <option value="online">Online</option>
                  <option value="office">Office</option>
                  <option value="client">Client Suggested</option>
                </select>
                <Input className="rounded-sm bg-secondary/40" value={l.label} onChange={(e) => { const ll = [...locations]; ll[i].label = e.target.value; setLocations(ll); }} />
                <button onClick={() => setLocations(locations.filter((_, ii) => ii !== i))} className="text-muted-foreground hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setLocations([...locations, { type: "office", label: "New location" }])}><Plus className="h-3 w-3 mr-1" />Add Location</Button>
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
        <Button className="rounded-sm bg-primary" onClick={() => mut.mutate()} disabled={mut.isPending}>Save</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function AvailabilityTab() {
  const { data, isLoading } = useQuery({ queryKey: ["avail"], queryFn: () => api.get("/meetings/availability").then((r) => r.data) });
  const [avail, setAvail] = useState(null);
  React.useEffect(() => { if (data && !avail) setAvail(data); }, [data, avail]);
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (payload) => api.put("/meetings/availability", payload),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["avail"] }); toast.success("Availability saved"); },
  });

  if (isLoading || !avail) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const update = (day, field, val) => setAvail({ ...avail, working_hours: { ...avail.working_hours, [day]: { ...avail.working_hours[day], [field]: val } } });

  return (
    <Card className="rounded-sm bg-card border-border p-6">
      <div className="font-display text-xl font-extrabold mb-4">Weekly Hours</div>
      <div className="space-y-2">
        {DAYS.map((d) => (
          <div key={d} className="flex items-center gap-3 border-b border-border/60 py-2">
            <Switch checked={avail.working_hours[d]?.enabled} onCheckedChange={(v) => update(d, "enabled", v)} />
            <div className="w-14 uppercase text-xs tracking-wider text-muted-foreground font-bold">{d}</div>
            {avail.working_hours[d]?.enabled ? (
              <>
                <Input type="time" value={avail.working_hours[d].start} onChange={(e) => update(d, "start", e.target.value)} className="rounded-sm w-32 bg-secondary/40" />
                <span className="text-muted-foreground">to</span>
                <Input type="time" value={avail.working_hours[d].end} onChange={(e) => update(d, "end", e.target.value)} className="rounded-sm w-32 bg-secondary/40" />
              </>
            ) : (
              <div className="text-sm text-muted-foreground">Off</div>
            )}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-4 mt-6">
        <div><Label>Buffer (min)</Label><Input type="number" className="rounded-sm mt-1.5 bg-secondary/40" value={avail.buffer_minutes} onChange={(e) => setAvail({ ...avail, buffer_minutes: Number(e.target.value) })} /></div>
        <div><Label>Min notice (min)</Label><Input type="number" className="rounded-sm mt-1.5 bg-secondary/40" value={avail.min_notice_minutes} onChange={(e) => setAvail({ ...avail, min_notice_minutes: Number(e.target.value) })} /></div>
        <div><Label>Max advance (days)</Label><Input type="number" className="rounded-sm mt-1.5 bg-secondary/40" value={avail.max_advance_days} onChange={(e) => setAvail({ ...avail, max_advance_days: Number(e.target.value) })} /></div>
      </div>
      <div className="flex justify-end mt-4">
        <Button data-testid={SCHED.saveAvailability} onClick={() => mut.mutate({ working_hours: avail.working_hours, buffer_minutes: avail.buffer_minutes, min_notice_minutes: avail.min_notice_minutes, max_advance_days: avail.max_advance_days })} className="rounded-sm bg-primary" disabled={mut.isPending}>Save</Button>
      </div>
    </Card>
  );
}

function BookedTab() {
  const { data: meetings = [] } = useQuery({ queryKey: ["meetings"], queryFn: () => api.get("/meetings").then((r) => r.data) });
  return (
    <div className="space-y-3">
      {meetings.length === 0 && <Card className="rounded-sm bg-card border-border p-8 text-center text-sm text-muted-foreground">No meetings booked yet.</Card>}
      {meetings.map((m) => {
        const start = new Date(m.start_at);
        return (
          <Card key={m.id} className="rounded-sm bg-card border-border p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">{m.meeting_type_name}</div>
                <div className="font-display text-lg font-extrabold">{m.contact_name}</div>
                <div className="text-sm text-muted-foreground">{m.contact_email}</div>
                {m.notes && <div className="text-xs text-muted-foreground mt-2">“{m.notes}”</div>}
              </div>
              <div className="text-right">
                <div className="font-display text-xl font-extrabold">{start.toLocaleDateString()}</div>
                <div className="text-sm text-primary">{start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                <Badge variant="secondary" className="rounded-sm mt-2 text-[10px]">{m.location?.label}</Badge>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
